import {useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {Loader, Separator, ShadowBox, Slider, YStack} from "@chayka/ui";
import {trpc} from "@/app/trpc";
import {BottomSheet, WpImage} from "@/components";
import {formatNewsDateLong, separateLinksFromText, transformHtml} from "@/pages/News/utils";

const NEWS_PREVIEW_COUNT = 5;

type NewsItem = (typeof trpc.news.list)["~types"]["output"][number];

export const NewsPreview = () => {
  const [selectedNews, setSelectedNews] = useState<NewsItem | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const {data, isLoading} = useQuery(
    trpc.news.list.queryOptions({offset: 0, count: NEWS_PREVIEW_COUNT}),
  );

  const contentRows = selectedNews ? separateLinksFromText(selectedNews.content) : undefined;

  const handleNewsClick = (item: NewsItem) => {
    setSelectedNews(item);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => {
      setSelectedNews(null);
    }, 1000);
  };

  const keyUsageMap = new Map<string, number>();

  const getPieceKey = (base: string) => {
    const usageCount = keyUsageMap.get(base) ?? 0;
    keyUsageMap.set(base, usageCount + 1);
    return `${base}-${usageCount}`;
  };

  return (
    <>
      <ShadowBox className="rounded-xl overflow-hidden self-stretch pb-4">
        {isLoading && (
          <YStack className="justify-center items-center h-80">
            <Loader />
          </YStack>
        )}
        {!isLoading && (
          <Slider
            withDots
            items={data || []}
            renderItem={({item}) => (
              <YStack
                key={item.id}
                className="w-full h-80 items-center justify-start relative"
                onClick={() => handleNewsClick(item)}
              >
                <div className="w-full h-80">
                  <WpImage
                    imageId={item.mediaId}
                    alt={transformHtml(item.title)}
                    className="w-full h-full object-cover"
                  />
                </div>
                <YStack className="gap-1 absolute bottom-0 left-1/2 transform -translate-x-1/2 bg-background w-full py-2 px-4">
                  <div className="w-full text-sm font-bold whitespace-nowrap overflow-hidden text-ellipsis">
                    {transformHtml(item.title)}
                  </div>
                  <div className="w-full text-sm whitespace-nowrap overflow-hidden text-ellipsis line-clamp-1">
                    {transformHtml(item.excerpt)}
                  </div>
                </YStack>
              </YStack>
            )}
          />
        )}
      </ShadowBox>
      <BottomSheet fullHeight isOpen={isOpen} onClose={handleClose}>
        <div className="flex w-full flex-col gap-4">
          {selectedNews?.mediaId ? (
            <WpImage
              imageId={selectedNews.mediaId}
              alt={transformHtml(selectedNews.title)}
              className="w-full object-cover aspect-square rounded-2xl"
            />
          ) : null}
          <h1 className="text-xl font-bold text-blackout">
            {selectedNews ? transformHtml(selectedNews.title) : "Новость"}
          </h1>
          {selectedNews?.date ? (
            <p className="text-sm text-secondary-gray">{formatNewsDateLong(selectedNews.date)}</p>
          ) : null}
          <Separator />
          {selectedNews ? (
            <YStack className="gap-1 whitespace-pre-line text-sm leading-relaxed text-blackout">
              {contentRows?.map((row, rowIndex) => {
                const rowKey = getPieceKey(`row-${rowIndex}`);

                return (
                  <p key={rowKey}>
                    {row.map((piece) => {
                      if (piece.type === "link") {
                        const key = getPieceKey(`link-${piece.url}-${piece.title}`);
                        return (
                          <a
                            key={key}
                            href={piece.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 underline"
                          >
                            {piece.title}
                          </a>
                        );
                      }

                      const key = getPieceKey(`text-${piece.text}`);
                      return <span key={key}>{piece.text}</span>;
                    })}
                  </p>
                );
              })}
            </YStack>
          ) : null}
        </div>
      </BottomSheet>
    </>
  );
};
